import { useEffect, useState } from 'react';
import wx from 'weixin-js-sdk'; // 需先安装：npm install weixin-js-sdk

/**
 * 获取完整图片URL
 * @param {string} imgUrl - 图片URL
 * @returns {string} 完整的图片URL
 */
export const getFullImageUrl = (imgUrl) => {
  // 处理空值情况
  if (!imgUrl) {
    return `${window.location.origin}/images/share_default_icon.png`;
  }

  // 处理绝对URL (支持 http 和 https)
  if (/^https?:\/\//.test(imgUrl)) {
    return imgUrl;
  }

  // 处理远程图片的相对路径
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
  if (process.env.REACT_APP_IMAGE_PATH_PREFIX && baseUrl) {
    if (imgUrl.startsWith(process.env.REACT_APP_IMAGE_PATH_PREFIX)) {
      // 确保 baseUrl 末尾没有斜杠，imgUrl 开头有斜杠
      const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const normalizedImgUrl = imgUrl.startsWith('/') ? imgUrl : '/' + imgUrl;
      
      return normalizedBaseUrl + normalizedImgUrl;
    }
  }

  // 处理其他相对路径 (确保以 / 开头)
  const normalizedImgUrl = imgUrl.startsWith('/') ? imgUrl : '/' + imgUrl;
  return `${window.location.origin}${normalizedImgUrl}`;
};

/**
 * 配置微信好友分享数据
 * @param {object} wxInstance - 微信JS-SDK实例
 * @param {object} params - 分享参数{title, desc, imgUrl, shareLink}
 */
export const configureAppMessageShare = (wxInstance, {title, desc, imgUrl, shareLink}) => {
  const currentUrl = shareLink || window.location.href.split('#')[0]; // 去除hash部分
  const imgFullUrl = getFullImageUrl(imgUrl);

  wxInstance.updateAppMessageShareData({
    title: title,
    desc: desc,
    link: currentUrl,
    imgUrl: imgFullUrl,
    success: () => {
      console.log('好友分享配置成功');
    },
    fail: (err) => {
      console.error('好友分享配置失败', err);
    }
  });
};

/**
 * 配置朋友圈分享数据
 * @param {object} wxInstance - 微信JS-SDK实例
 * @param {object} params - 分享参数{title, imgUrl, shareLink}
 */
export const configureTimelineShare = (wxInstance, {title, imgUrl, shareLink}) => {
  const currentUrl = shareLink || window.location.href.split('#')[0]; // 去除hash部分
  const imgFullUrl = getFullImageUrl(imgUrl);

  wxInstance.updateTimelineShareData({
    title: title, // 朋友圈分享标题（无描述字段）
    link: currentUrl,
    imgUrl: imgFullUrl,
    success: () => {
      console.log('朋友圈分享配置成功');
    },
    fail: (err) => {
      console.error('朋友圈分享配置失败', err);
    }
  });
};

/**
 * 配置微信分享数据（包括好友和朋友圈）
 * @param {object} wxInstance - 微信JS-SDK实例
 * @param {object} params - 分享参数{title, desc, imgUrl, shareLink}
 */
export const configureWxShareData = (wxInstance, params) => {
  // 配置好友分享
  configureAppMessageShare(wxInstance, params);
  
  // 配置朋友圈分享
  configureTimelineShare(wxInstance, params);
};

/**
 * 微信分享Hook（对接JS-SDK）
 * @param {string} title - 分享标题
 * @param {string} desc - 分享描述
 * @param {string} imgUrl - 分享图片（必须是完整URL，且域名在公众号安全域名内）
 * @param {string} [shareLink] - 自定义分享链接（可选，默认用当前页面URL）
 * @param {function} [roadCallbackFn] - 自定义加载后的回调函数（可选）
 * @returns {object} - { wx: 模块内wx对象, configured: 配置状态（响应式） }
 */
export const useWechatShare = (title, desc, imgUrl, shareLink, roadCallbackFn) => {
  const [wxConfigured, setWxConfigured] = useState(false); // 响应式状态，支持组件重新渲染

  useEffect(() => {
    if (wxConfigured) return; // 已配置完成，跳过

    // 1. 获取当前页面URL（用于签名）
    const currentUrl = shareLink || window.location.href.split('#')[0]; // 去除hash部分
  
    // 2. 调用后端接口获取微信签名
    fetch(`/qhkj-agri-map-api/wechat/share/sign?url=${encodeURIComponent(currentUrl)}`)
      .then(res => {
        if (!res.ok) throw new Error('获取签名失败');
        return res.json();
      })
      .then(response => {
        const data = response.data;
        // 3. 配置微信JS-SDK
        wx.config({
          debug: false, // 生产环境关闭调试模式（true时会弹出调试信息）
          appId: data.appId, // 公众号的appId（从后端获取）
          timestamp: data.timestamp, // 时间戳
          nonceStr: data.nonceStr, // 随机字符串
          signature: data.signature, // 签名
          jsApiList: [
            'updateAppMessageShareData', // 分享给朋友
            'updateTimelineShareData',    // 分享到朋友圈,
            'openLocation' // 导航必需权限
          ]
        });

        // 4. 配置分享内容
        wx.ready(() => {
          console.log('微信JS-SDK配置成功');
          setWxConfigured(true);
          
          // 使用抽取的方法配置分享数据
          configureWxShareData(wx, {title, desc, imgUrl, shareLink});

          // 执行自定义回调
          if (roadCallbackFn && typeof roadCallbackFn === 'function') {
            roadCallbackFn();
          }
        });

        // 5. 监听JS-SDK错误
        wx.error((err) => {
          console.error('微信JS-SDK错误', err);
          // 在生产环境中避免使用 alert，或者提供更友好的错误提示方式
          if (process.env.NODE_ENV !== 'production') {
            alert(`微信JS-SDK错误，请检查配置是否正确`);
          }
          setWxConfigured(false);
        });
      })
      .catch(err => {
        console.error('微信分享初始化失败', err);
        // 在生产环境中避免使用 alert，或者提供更友好的错误提示方式
        if (process.env.NODE_ENV !== 'production') {
          alert(`微信分享初始化失败，请检查后端接口是否正确`);
        }
        setWxConfigured(false);
      });
  }, [title, desc, imgUrl, shareLink]);
  
  // 返回响应式状态和wx对象
  return { wx, configured: wxConfigured, updateShareData: configureWxShareData };
};

/**
 * 更新微信分享的配置信息
 * @param {object} wx - 微信JS-SDK对象
 * @param {object} params - 分享参数{title, desc, imgUrl, shareLink}
 */
export const updateWxShareData = (wx, params) => {
  // 使用外部定义的 configureWxShareData 方法
  configureWxShareData(wx, params);
};